import React, { useEffect, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { FieldWrapper } from "../components/FieldWrapper";
import { BaseField } from "./BaseField";
import {
    DefaultValue,
    Description,
    HideInPdf,
    Hint,
    isHiddenInPdf,
    MaxLength,
    ReadOnly,
    Required,
    Width,
} from "./configurables";
import { AutoFill } from "./configurables/AutoFill";
import { Conditional } from "./configurables/Conditional";
import { KeepExisting } from "./configurables/KeepExisting";
import { NonMigratable } from "./configurables/NonMigratable";
import { NonTemplatable } from "./configurables/NonTemplatable";
import { Label, LabelInput } from "./inputFields/label";
import { getInventoryItemsAJAX, getInventoryItemsForFormAJAX } from "../actions/inventory_items";

export class InventoryItemSelectField extends BaseField {
    public name = "inventory_item_select";
    public label = "LeistungsV";
    public icon = "archive";
    public fieldType = "LeistungsV";

    public getConfigurables() {
        return [
            AutoFill,
            KeepExisting,
            NonMigratable,
            NonTemplatable,
            Conditional,
            MaxLength,
            Required,
            Description,
            DefaultValue,
            Width,
            Hint,
            ReadOnly,
            HideInPdf,
        ];
    }

    /**
     * Renders the field in edit mode
     * @param designer
     * @param schema
     * @param register Registers the input field in the global form submission
     * @param field
     */
    public EditComponent({ designer, schema, register, field, provided = null }) {
        return (
            <FieldWrapper
                register={register}
                provided={provided}
                field={field}
                schema={schema}
                designer={designer}
            >
                <div>
                    <LabelInput register={register} field={field} />
                </div>
            </FieldWrapper>
        );
    }

    /**
     * Renders the component to input data in it.
     * @param layout
     * @param schema
     * @param field
     * @param entry
     * @param register
     * @param name
     */
    public InputComponent({
        layout,
        schema,
        field,
        form,
        register,
        name,
        rawValue,
        readonly = false,
        variables = {},
        saveState = {},
    }: any) {
        const [invItems, setInvItems] = useState([]);
        const fieldName = name || field.hashCode;
        const notesKey = `${field.hashCode}__notes`;
        const notesValue = saveState?.[notesKey] || "";
        const notesText = typeof notesValue === "string" ? notesValue : "";
        const hasNotes = notesText.trim().length > 0;
        const hideMangelConfig = schema?.getConfig?.()?.hideMangelLabel;
        const hideMangelLabel = hideMangelConfig === true || hideMangelConfig === "on";
        const canCreate = typeof variables?.onCreateInventoryItem === "function" && !readonly;
        const skipResetRef = useRef(false);

        useEffect(() => {
            // Offline-first: if the parent (app) injected items from local SQLite, trust them
            // even if empty. Web never passes this prop, so it falls through to the API path.
            if (Array.isArray(variables?.inventoryItems)) {
                setInvItems(variables.inventoryItems);
                return;
            }

            // Get form_id from multiple possible sources
            const formId = variables?.form?.id || form?.id || form;

            if (formId) {
                getInventoryItemsForFormAJAX({}, formId)
                    .then((result) => {
                        setInvItems(result.data.inventory_items);
                    })
                    .catch((err) => console.error(err));
            } else {
                // Fallback to all items if no form_id available
                getInventoryItemsAJAX({ page_size: -1 })
                    .then((result) => {
                        setInvItems(
                            result.data?.inventory_items || result.data?.inventoryItems || []
                        );
                    })
                    .catch((err) => console.error(err));
            }
        }, [variables?.inventoryItems, variables?.form?.id, form]);

        // react-select expects {value, label}
        const options = invItems.map((i: any) => ({
            value: i.short_name,
            label: i.short_name,
            cell: i,
        }));

        // Determine initial values for controlled component
        const initialSelected = () => {
            if (rawValue) {
                // Support both array and csv
                if (Array.isArray(rawValue)) {
                    const values = rawValue
                        .map((val) => (typeof val === "string" ? val : val?.value || val?.name))
                        .filter(Boolean);
                    return options.filter((opt) => values.includes(opt.value));
                } else if (typeof rawValue === "string") {
                    try {
                        const parsed = JSON.parse(rawValue);
                        if (Array.isArray(parsed)) {
                            const values = parsed
                                .map((val) =>
                                    typeof val === "string" ? val : val?.value || val?.name
                                )
                                .filter(Boolean);
                            return options.filter((opt) => values.includes(opt.value));
                        }
                    } catch (e) {
                        const values = rawValue.split(",").map((v) => v.trim());
                        return options.filter((opt) => values.includes(opt.value));
                    }
                }
            }
            return [];
        };
        const [selectedOptions, setSelectedOptions] = useState(initialSelected);
        const selectedValueJson = JSON.stringify(
            (selectedOptions || []).map((o: any) => ({
                value: o.value,
                label: o.label,
                cell: o.cell,
            }))
        );

        useEffect(() => {
            if (skipResetRef.current) {
                skipResetRef.current = false;
                return;
            }
            setSelectedOptions(initialSelected());
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [invItems, rawValue]);

        // Handler for react-select
        const handleChange = (selected: any) => {
            setSelectedOptions(selected || []);
        };

        const optionLabel = (o: any) =>
            o?.cell?.approved === false ? `${o.label} (unter Vorbehalt)` : o.label;

        const isValidNewOption = (inputValue: string) => {
            if (!canCreate) {
                return false;
            }
            const needle = (inputValue || "").trim().toLowerCase();
            if (!needle) {
                return false;
            }
            return !options.some((o) => String(o.value).toLowerCase() === needle);
        };

        const handleCreate = async (inputValue: string) => {
            const shortName = (inputValue || "").trim();
            if (!shortName) {
                return;
            }
            let item: any = null;
            try {
                item = await variables.onCreateInventoryItem({
                    short_name: shortName,
                    form: variables?.form || form,
                });
            } catch (e) {
                item = null;
            }
            if (!item) {
                return;
            }
            const option = { value: item.short_name, label: item.short_name, cell: item };
            skipResetRef.current = true;
            setInvItems((prev: any[]) => [...prev, item]);
            setSelectedOptions((prev: any[]) => [...(prev || []), option]);
        };

        if (variables?.isPrinting && isHiddenInPdf(field)) {
            return <></>;
        }

        return (
            <>
                {layout.wrapLabel(field.configureLabel(<Label layout={layout} field={field} />))}
                {layout.wrapDescription(
                    field.rawOptions.description ? <p>{field.rawOptions.description}</p> : <></>
                )}
                {rawValue && !(window as any).EDIT_MODE && (
                    <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {(() => {
                            const toLabel = (val: any) => {
                                if (typeof val === "string") return val;
                                if (typeof val?.label === "string") return val.label;
                                if (typeof val?.value === "string") return val.value;
                                if (typeof val?.name === "string") return val.name;
                                return null;
                            };
                            if (Array.isArray(rawValue)) {
                                const labels = rawValue.map(toLabel).filter(Boolean);
                                return labels.join(", ");
                            }
                            if (typeof rawValue === "string") {
                                try {
                                    const parsed = JSON.parse(rawValue);
                                    if (Array.isArray(parsed)) {
                                        const labels = parsed.map(toLabel).filter(Boolean);
                                        return labels.length > 0 ? labels.join(", ") : rawValue;
                                    }
                                } catch (e) {
                                    return rawValue;
                                }
                            }
                            return toLabel(rawValue) || "";
                        })()}
                    </p>
                )}
                {!(window as any).EDIT_MODE && hasNotes && (
                    <div style={{ marginTop: 8 }}>
                        {!hideMangelLabel && <strong>Beschreibung Mängel:</strong>}
                        <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {notesText}
                        </div>
                    </div>
                )}
                {(!rawValue || (window as any).EDIT_MODE) &&
                    layout.wrapInput(
                        field.configureInput(
                            <>
                                <CreatableSelect
                                    {...(schema && schema.selectProps ? schema.selectProps : {})}
                                    options={options}
                                    isMulti
                                    value={selectedOptions}
                                    onChange={handleChange}
                                    isDisabled={readonly}
                                    isValidNewOption={isValidNewOption}
                                    onCreateOption={handleCreate}
                                    formatCreateLabel={(v: string) =>
                                        `„${v}" als neue Position anlegen (unter Vorbehalt)`
                                    }
                                    formatOptionLabel={optionLabel}
                                    placeholder="Bitte auswählen..."
                                    name={`${fieldName}-select`}
                                    inputId={`${fieldName}-select`}
                                    menuPlacement="auto"
                                    menuShouldScrollIntoView={true}
                                    classNamePrefix="rps-combo"
                                />
                                <input
                                    type="hidden"
                                    name={fieldName}
                                    value={selectedValueJson}
                                    ref={register}
                                />
                                <div style={{ marginTop: 12 }}>
                                    <label
                                        style={{ display: "block", fontSize: 13, color: "#222" }}
                                    >
                                        Beschreibung Mängel
                                    </label>
                                    <textarea
                                        name={notesKey}
                                        ref={register}
                                        defaultValue={notesValue}
                                        className="form-control"
                                        rows={3}
                                        readOnly={readonly}
                                    />
                                </div>
                                <div style={{ marginTop: 8, fontSize: 13, color: "#222" }}>
                                    {selectedOptions.length > 0
                                        ? selectedOptions.map(optionLabel).join(", ")
                                        : "Keine Auswahl"}
                                </div>
                            </>,
                            register,
                            schema
                        )
                    )}
                {!rawValue && <div className={"visible-print"}>-</div>}
                {layout.wrapHint(field.rawOptions.hint ? <p>{field.rawOptions.hint}</p> : <></>)}
            </>
        );
    }

    public PDFComponent({ field, styles }) {
        return <></>;
    }
}
